import React, { useEffect, useState } from "react";
import "./growthChartGraph.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register({
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
});

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    hover: {
      mode: "index",
      intersect: false,
    },
    tooltip: {
      enabled: false,
    },
    title: {
      display: true,
      text: "",
      color: "#212121",
      font: {
        family: "Rubik, sans-serif",
        weight: 500,
        size: 13,
      },
    },
  },
  scales: {
    x: {
      type: "linear",
      title: {
        display: true,
        text: "",
        color: "#17BAE0",
        font: {
          family: "Rubik, sans-serif",
          weight: 500,
          size: 13,
        },
      },
      ticks: {
        stepSize: 0.5,
        color: "#1D1D1D",
        font: {
          family: "Rubik, sans-serif",
          size: 10,
        },
      },
      grid: {
        display: false,
      },
    },

    x1: {
      type: "linear",
      title: {
        display: false,
      },
      ticks: {
        display: false,
        stepSize: 0.5,
      },
      grid: {
        display: false,
        drawBorder: false,
      },
    },

    y: {
      position: "left",
      title: {
        display: true,
        text: "",
        color: "#17BAE0",
        font: {
          family: "Rubik, sans-serif",
          weight: 500,
          size: 13,
        },
      },
      grid: {
        display: false,
      },
      ticks: {
        stepSize: 2,
        color: "#1D1D1D",
        font: {
          family: "Rubik, sans-serif",
          size: 10,
        },
      },
    },

    y1: {
      position: "right",
      ticks: {
        display: false,
        stepSize: 0.1,
        color: "#242424",
        font: {
          family: "Rubik, sans-serif",
          weight: 500,
          size: 8,
        },
      },
      grid: {
        display: false,
        drawBorder: false,
      },
    },
  },
};

const labels = [];

const data = {
  labels,
  datasets: [
    {
      fill: false,
      data: [],
      borderColor: "black",
      borderWidth: 1,
      pointBorderWidth: 1,
      pointRadius: 2.5,
      pointBorderColor: "#fff",
      pointBackgroundColor: "#151515",
      yAxisID: "y",
      xAxisID: "x",
    },
    {
      fill: true,
      data: [],
      backgroundColor: "#17BAE033",
      pointRadius: 0,
      borderWidth: 0,
      yAxisID: "y1",
      xAxisID: "x1",
    },
    {
      fill: true,
      data: [],
      backgroundColor: "#17BAE033",
      pointRadius: 0,
      borderWidth: 0,
      yAxisID: "y1",
      xAxisID: "x1",
    },
    {
      fill: true,
      data: [],
      backgroundColor: "#17BAE01A",
      pointRadius: 0,
      borderWidth: 0,
      yAxisID: "y1",
      xAxisID: "x1",
    },
    {
      fill: true,
      data: [],
      backgroundColor: "#17BAE01A",
      pointRadius: 0,
      borderWidth: 0,
      yAxisID: "y1",
      xAxisID: "x1",
    },
    {
      fill: true,
      data: [],
      backgroundColor: "#E4F3F7",
      pointRadius: 0,
      borderWidth: 0,
      yAxisID: "y1",
      xAxisID: "x1",
    },
  ],
};

export const GrowthChartGraph = (props) => {
  const {
    graphDetails,
    growthChartTiles,
    patientVisits,
    getZscore,
    getTilesZscore,
  } = props;

  const xDataPoints = window.innerWidth > 767 ? 8 : 5;
  const yDataPoints = window.innerWidth > 767 ? 11 : 9;

  const [opt, setOpt] = useState(options);
  const [chartData, setChartData] = useState(data);
  const [chartAge, setChartAge] = useState("");
  const [chartPercentile, setChartPercentile] = useState("");

  // Lifecycle Methods ===>

  useEffect(() => {
    handleSelectedGrowthTile();
  }, [growthChartTiles, patientVisits]);

  useEffect(() => {
    if (getZscore) {
      handleZscore();
    }
  }, [getZscore]);

  // <===

  // Helper Methods ===>

  const handleZscore = () => {
    growthChartTiles.forEach((ele) => {
      ele.zScore = handleSelectedGrowthTile(ele);
    });
    getTilesZscore(growthChartTiles);
  };

  const handleSelectedGrowthTile = (tileElement) => {
    let tile = tileElement ?? growthChartTiles.filter((ele) => ele.selected)[0];
    let score = "";

    let filteredGraphData = patientVisits.filter(
      (ele) => ele.patientAgeInYears <= 18
    );

    filteredGraphData.forEach((ele) => {
      ele.patientAgeInMonths = Number((ele.patientAgeInYears * 12).toFixed(2));
    });

    score = handleTileData(
      filteredGraphData,
      tile,
      tile.xSteps,
      tile.ySteps,
      tile.xData,
      tile.yData
    );

    if (getZscore) {
      return score;
    }
  };

  const handleTileData = (
    filteredGraphData,
    tile,
    xSteps,
    ySteps,
    xData,
    yData
  ) => {
    let labels = [],
      values = [],
      range = [],
      age = "",
      percentile = "",
      xRef = 0,
      yRef = 0,
      xMin = 0,
      xMax =
        window.innerWidth > 767
          ? xData.includes("patientAge")
            ? 4
            : 40
          : xData.includes("patientAge")
          ? 2.5
          : 25,
      yMin = 0,
      yMax = window.innerWidth > 767 ? 22 : 18,
      xText = tile.xText + (xData.includes("patientAge") ? " (Years)" : ""),
      p3 = [],
      p25 = [],
      p50 = [],
      p75 = [],
      p97 = [];

    let selectedVisit = patientVisits.filter((d) => d.selected);

    filteredGraphData = filteredGraphData.filter(
      (d) => (!xData.includes("patientAge") ? d[xData] : true) && d[yData]
    );

    let allow = true;
    if (selectedVisit.length !== 0) {
      let selectedData = filteredGraphData.filter((d) => d.selected);
      allow = selectedData.length !== 0;
    }

    if (filteredGraphData.length !== 0 && allow) {
      let yAxis =
        yData === "weight" || yData === "bmi"
          ? filteredGraphData.filter(
              (ele) =>
                Number(ele[yData]) ===
                Math.max(...filteredGraphData.map((o) => Number(o[yData])))
            )[0]
          : filteredGraphData[0];

      let data = selectedVisit.length !== 0 ? selectedVisit[0] : yAxis;
      xRef = Number(data[xData]);
      yRef = Number(data[yData]);

      let years = data.patient_age.split("years"),
        months = years[1].split("months"),
        days = months[1].split("days");

      let totalMonths =
        Number(months[0].trim()) + (Number(days[0].trim()) >= 30 ? 1 : 0);
      totalMonths = Number(years[0].trim()) * 12 + totalMonths;

      if (xData.includes("patientAge")) {
        if (xRef <= 2) {
          xData = "patientAgeInMonths";
          xRef = totalMonths;
          xText = tile.xText + " (Months)";
        }

        let xRefNum = Number(xRef.toFixed(1).split(".")[1]);
        if (xRefNum > 0 && xRefNum < 5) {
          xRef = Number(xRef.toString().split(".")[0]) + 0.5;
        } else if (xRefNum > 5) {
          xRef = Number(xRef.toString().split(".")[0]) + 1;
        }
      } else {
        let xRefNum = Number(xRef.toString().split(".")[0]);
        xRef =
          xRefNum +
          (xRefNum % 5 === 0 ? 5 : Math.ceil(xRefNum / 5) * 5 - xRefNum);
      }

      if (yData === "muac") {
        let yRefNum = Number(yRef.toString().split(".")[0]);
        yRef = yRefNum + (yRefNum % 2 === 0 ? 2 : 1);
      }

      if (getZscore) {
        let xVal = xData.includes("patientAge") ? totalMonths : data[xData];
        return totalMonths <= 60 ? getZscoreData(data, tile, xVal) : "";
      } else {
        xMin = xRef - (xData.includes("patientAge") ? 2 : xSteps * xDataPoints);
        xMin = getTruncateValue(xMin, 1);
        xMin = xMin > 0 ? xMin : 0;
        xMax = xMin + xSteps * xDataPoints;

        if (yData === "muac") {
          yMin = yRef - ySteps * yDataPoints;
          yMin = yMin > 0 ? yMin : 0;
          yMax = yMin + ySteps * yDataPoints;
        }

        if (xData.includes("patientAgeInYears") && xMax > 18) {
          xMax = 18;
          xMin = xMax - xSteps * xDataPoints;
        }

        let yMaxVal = false;
        if (
          selectedVisit.length !== 0 &&
          yData !== "weight" &&
          yData !== "bmi"
        ) {
          yMaxVal = true;
        }

        filteredGraphData = filteredGraphData.filter(
          (ele) =>
            (yMaxVal ? ele[yData] <= yRef : true) &&
            (yData === "muac"
              ? ele[yData] >= yMin && ele[yData] <= yMax
              : true) &&
            ele[xData] >= xMin &&
            ele[xData] <= xMax
        );

        labels = filteredGraphData.map((item) => {
          return item[xData];
        });
        values = filteredGraphData.map((item) => {
          return item[yData];
        });

        let minMaxRange = getValuesForPercentile(xMin, xMax, xData);

        if (xData === "patientAgeInYears") {
          minMaxRange = [minMaxRange[0] * 12, minMaxRange[1] * 12];
        }

        if (xData.includes("patientAgeInMonths")) {
          range = Array.apply(null, {
            length: (minMaxRange[1] - minMaxRange[0]) / 0.1 + 1,
          }).map((_, idx) => {
            return Number((minMaxRange[0] + idx * 0.1).toFixed(1));
          });
        }

        let percentileData = getPercentileData(
          data,
          tile,
          minMaxRange,
          range.length !== 0 ? range : null
        );

        p3 = percentileData[0];
        p25 = percentileData[1];
        p50 = percentileData[2];
        p75 = percentileData[3];
        p97 = percentileData[4];

        let originalRange =
          percentileData[5].length !== 0 ? percentileData[5] : range;

        if (!xData.includes("patientAgeInMonths")) {
          range = JSON.parse(JSON.stringify(originalRange));
        } else {
          if (
            xData.includes("patientAgeInMonths") &&
            (data[xData].toString().includes(".98") ||
              data[xData].toString().includes(".99") ||
              !data[xData].toString().split(".")[1])
          ) {
            range = JSON.parse(JSON.stringify(originalRange));
          }
        }

        if (range.length !== 0 && range[0] !== minMaxRange[0]) {
          let arr = Array.apply(null, {
            length: (range[0] - minMaxRange[0]) / xSteps,
          }).map((_, idx) => {
            return minMaxRange[0] + idx * xSteps;
          });
          originalRange = [...arr, ...range];
        }
        if (range.length !== 0 && range[range.length - 1] !== minMaxRange[1]) {
          let arr = Array.apply(null, {
            length: (minMaxRange[1] - range[range.length - 1]) / xSteps,
          }).map((_, idx) => {
            return range[range.length - 1] + (idx + 1) * xSteps;
          });
          originalRange = [...range, ...arr];
        }

        if (xData === "patientAgeInYears") {
          range = range.map((ele) => {
            return ele / 12;
          });
        }

        if (p3.length !== 0) {
          let mindata = Math.min(
            ...filteredGraphData.map((o) => Number(o[yData]))
          );

          let minVal = Math.floor(
            Math.min(mindata, Math.min(...p3.map((o) => Number(o))))
          );
          let maxVal = Math.ceil(
            Math.max(yRef, Math.max(...p97.map((o) => Number(o))))
          );
          ySteps = Math.ceil((maxVal - minVal) / (yDataPoints + 1));
          yMin = minVal;
          yMax = yMin + ySteps * yDataPoints;

          if (minVal === mindata) {
            if (Math.floor(minVal - ySteps / 2) < 0) {
              minVal = 0;
            } else {
              minVal = Math.floor(minVal - ySteps / 2);
              maxVal = Math.ceil(maxVal + ySteps / 2);
            }
          }

          if (maxVal === yRef || yRef >= yMax) {
            minVal =
              Math.floor(minVal - ySteps / 2) < 0
                ? 0
                : Math.floor(minVal - ySteps / 2);
            maxVal = Math.ceil(maxVal + ySteps / 2);
          }

          ySteps = Math.ceil((maxVal - minVal) / (yDataPoints + 1));
          yMin = minVal;
          yMax = yMin + ySteps * yDataPoints;
        }

        p97 = p97.map((ele) => {
          return ele > yMax ? yMax : ele;
        });

        age = getAgeValueOnTab(data);
        percentile = getPercentileValueOnTab(
          data,
          xData,
          yData,
          originalRange,
          tile,
          totalMonths
        );
      }
    }

    if (!getZscore) {
      let chartTitle = tile?.title?.replace(/-/g, " ").split(" ");
      chartTitle = chartTitle
        .map((ele) => ele[0].toUpperCase() + ele.substr(1))
        .join(" ");

      options.plugins.title.text = window.innerWidth > 767 ? chartTitle : "";
      options.scales.x.suggestedMin = xMin;
      options.scales.x.suggestedMax = xMax;
      options.scales.x1.suggestedMin = xMin;
      options.scales.x1.suggestedMax = xMax;
      options.scales.y.min = yMin;
      options.scales.y.max = yMax;
      options.scales.y1.min = yMin;
      options.scales.y1.max = yMax;
      options.scales.x.title.text = xText;
      options.scales.y.title.text = tile?.yText;
      options.scales.x.ticks.stepSize = xSteps;
      options.scales.x1.labels = range;
      options.scales.x1.ticks.stepSize = xSteps;
      options.scales.y.ticks.stepSize = ySteps;
      options.scales.y1.ticks =
        p3.length !== 0
          ? {
              ...options.scales.y1.ticks,
              display: true,
              autoSkip: false,
              callback: (val) => {
                return getPercentileTicks(val, p3, p25, p50, p75, p97);
              },
            }
          : { ...options.scales.y1.ticks, display: false };

      data.labels = labels;
      data.datasets[0].data = values;
      data.datasets[1].data = p3;
      data.datasets[2].data = p25;
      data.datasets[3].data = p50;
      data.datasets[4].data = p75;
      data.datasets[5].data = p97;

      if (selectedVisit.length !== 0) {
        let selectedInd = filteredGraphData.findIndex((ele) => ele.selected);
        let radius = Array(filteredGraphData.length).fill(2.5);
        let borderColor = Array(filteredGraphData.length).fill("#fff");
        if (selectedInd !== -1) {
          radius[selectedInd] = 3;
          borderColor[selectedInd] = "#FF1DDC";
        }
        data.datasets[0].pointRadius = radius;
        data.datasets[0].pointBorderColor = borderColor;
      } else {
        data.datasets[0].pointRadius = 2.5;
        data.datasets[0].pointBorderColor = "#fff";
      }

      setOpt({ ...options });
      setChartData({ ...data });
      setChartAge(age);
      setChartPercentile(percentile);
    }
  };

  const getTruncateValue = (val, places) => {
    return Math.trunc(val * Math.pow(10, places)) / Math.pow(10, places);
  };

  const getZscoreData = (data, tile, val) => {
    let key =
      tile?.title?.replace(/-/g, "_")?.toLowerCase() +
      (data?.patient_gender?.toLowerCase() === "female" ? "_girls" : "_boys");
    let score = "";
    if (graphDetails) {
      let zscores = graphDetails[0]?.growth_graph_zscore[key];
      score = zscores?.filter(
        (ele, ind) =>
          val >= ele.value &&
          (zscores[ind + 1] ? val < zscores[ind + 1]?.value : val <= ele.value)
      );

      if (score && score.length !== 0) {
        score = score[0].zscore;
      } else {
        score = "";
      }
    }

    return score;
  };

  const getValuesForPercentile = (xMin, xMax, xData) => {
    let minRange = xMin,
      maxRange = xMax;

    if (xData.includes("patientAge")) {
      let minDecimal = getTruncateValue(minRange, 1);
      minDecimal = Number(minDecimal.toString().split(".")[1]);
      let maxDecimal = getTruncateValue(maxRange, 1);
      maxDecimal = Number(maxDecimal.toString().split(".")[1]);

      if (minDecimal > 0 && minDecimal < 5) {
        minRange = Number(minRange.toString().split(".")[0]);
      } else if (minDecimal > 5) {
        minRange = Number(minRange.toString().split(".")[0]) + 0.5;
      }

      if (maxDecimal > 0 && maxDecimal < 5) {
        maxRange = Number(maxRange.toString().split(".")[0]) + 0.5;
      } else if (maxDecimal > 5) {
        maxRange = Number(maxRange.toString().split(".")[0]) + 1;
      }
    } else {
      minRange = Math.ceil(xMin / 5) * 5 - ((xMin / 5) % 1 !== 0 ? 5 : 0);
      maxRange = Math.ceil(xMax / 5) * 5;
    }

    return [minRange, maxRange];
  };

  const getAgeValueOnTab = (ageData) => {
    let age = "",
      years = ageData.patient_age.split("years"),
      months = years[1].split("months"),
      days = months[1].split("days");

    let ageInYears = Number(years[0].trim());
    let ageInMonths = Number(months[0].trim());
    let ageInDays = Number(days[0].trim());

    let totalMonths = ageInMonths + (ageInDays >= 30 ? 1 : 0);

    if (ageInYears === 0 && totalMonths < 1) {
      age = ageInDays + " D";
    } else if (totalMonths < 12 && ageInYears < 1) {
      age = totalMonths.toString() + " M";
    } else {
      age =
        ageInYears +
        (totalMonths >= 12 ? 1 : 0) +
        " Y" +
        (totalMonths === 0 || totalMonths === 12
          ? ""
          : " " +
            (totalMonths >= 12 ? totalMonths - 12 + " M" : totalMonths + " M"));
    }
    return age;
  };

  const getPercentileValueOnTab = (
    data,
    xData,
    yData,
    range,
    tile,
    totalMonths
  ) => {
    let value = "",
      element = 0,
      obj = {},
      xVal =
        xData.includes("patientAge") &&
        (data["patientAgeInMonths"].toString().includes(".98") ||
          data["patientAgeInMonths"].toString().includes(".99"))
          ? totalMonths
          : data[xData],
      yVal = data[yData];

    if (
      xData === "patientAgeInYears" &&
      !data["patientAgeInMonths"].toString().includes(".98") &&
      !data["patientAgeInMonths"].toString().includes(".99")
    ) {
      xVal = Number((xVal * 12).toFixed(2));
    }

    let key =
      tile?.title?.replace(/-/g, "_")?.toLowerCase() +
      (data?.patient_gender?.toLowerCase() === "female" ? "_girls" : "_boys");

    range.forEach((ele, ind) => {
      if (xVal >= ele && (range[ind + 1] ? xVal < range[ind + 1] : true)) {
        element = ele;
      }
    });

    if (graphDetails) {
      let percentiles = graphDetails[0]?.growth_graph_percentile[key];
      let val = percentiles?.filter((ele) => ele.value === element);
      obj = val ? val[0] : null;
    }

    if (obj) {
      if (yVal <= obj.P3) {
        value = "3";
      } else if (yVal > obj.P3 && yVal <= obj.P25) {
        value = "25";
      } else if (yVal > obj.P25 && yVal <= obj.P50) {
        value = "50";
      } else if (yVal > obj.P50 && yVal <= obj.P75) {
        value = "75";
      } else if (yVal > obj.P75 && yVal <= obj.P97) {
        value = "97";
      } else if (yVal > obj.P97) {
        value = "100";
      }
    }

    return value;
  };

  const getPercentileTicks = (val, p3, p25, p50, p75, p97) => {
    let label = "";

    if (val === getTruncateValue(Number(p3[p3.length - 1]), 1)) {
      label = "P3";
    } else if (val === getTruncateValue(Number(p25[p25.length - 1]), 1)) {
      label = "P25";
    } else if (val === getTruncateValue(Number(p50[p50.length - 1]), 1)) {
      label = "P50";
    } else if (val === getTruncateValue(Number(p75[p75.length - 1]), 1)) {
      label = "P75";
    } else if (val === getTruncateValue(Number(p97[p97.length - 1]), 1)) {
      label = "P97";
    }

    return label;
  };

  const getPercentileData = (percentileData, tile, minMaxRange, range) => {
    let key =
      tile?.title?.replace(/-/g, "_")?.toLowerCase() +
      (percentileData?.patient_gender?.toLowerCase() === "female"
        ? "_girls"
        : "_boys");
    let p3 = [],
      p25 = [],
      p50 = [],
      p75 = [],
      p97 = [],
      originalRange = [];

    if (graphDetails) {
      let percentiles = graphDetails[0]?.growth_graph_percentile[key];
      if (range) {
        range.forEach((item) => {
          let val = null;

          percentiles &&
            percentiles.forEach((ele, ind) => {
              if (
                item >= ele.value &&
                (percentiles[ind + 1]
                  ? item < percentiles[ind + 1].value
                  : true)
              ) {
                val = ele;
              }
            });
          if (val && val.length !== 0) {
            originalRange.push(val.value);
            p3.push(val.P3);
            p25.push(val.P25);
            p50.push(val.P50);
            p75.push(val.P75);
            p97.push(val.P97);
          }
        });
      } else {
        let val = percentiles?.filter(
          (ele) => ele.value >= minMaxRange[0] && ele.value <= minMaxRange[1]
        );
        if (val && val.length !== 0) {
          val.forEach((item) => {
            originalRange.push(item.value);
            p3.push(item.P3);
            p25.push(item.P25);
            p50.push(item.P50);
            p75.push(item.P75);
            p97.push(item.P97);
          });
        }
      }
    }

    return [p3, p25, p50, p75, p97, originalRange];
  };

  // <===

  return (
    <div className="growth-chart-graph">
      {(chartAge || chartPercentile) && (
        <div className="growth-chart-graph-tab">
          {chartAge && <span>Age: {chartAge}</span>}
          {chartPercentile && (
            <span>
              {chartAge ? "," : ""} Percentile: {chartPercentile}
            </span>
          )}
        </div>
      )}
      <Line options={opt} data={chartData} redraw />
    </div>
  );
};
